// src/components/AssistantSettings.js — Settings → AI Assistant (super admins)
// [marker: ai-assistant v1]
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function AssistantSettings() {
  const [s, setS] = useState(null);
  const [key, setKey] = useState('');
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get('/assistant/settings').then(r => setS(r.data)).catch(err => toast.error(err.response?.data?.error || 'Could not load.'));
    api.get('/assistant/log').then(r => setLog(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!s) return <div className="spinner" />;
  const set = (k, v) => setS(x => ({ ...x, [k]: v }));
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      await api.put('/assistant/settings', { enabled: s.enabled, model: s.model, staffAccess: s.staffAccess, monthlyLimit: s.monthlyLimit, ...(key.trim() ? { apiKey: key.trim() } : {}), ...extra });
      toast.success('Saved.'); setKey(''); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { const r = await api.post('/assistant/test'); toast.success(`Working — ${r.data.model} answered “${r.data.reply}”.`); }
    catch (err) { toast.error(err.response?.data?.error || 'Test failed.'); }
    finally { setBusy(false); }
  };
  const row = { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 12, alignItems: 'center', marginBottom: 14 };
  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 900 }}>
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>AI Assistant</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--gray-600)', marginTop: 0 }}>
          The <strong>✨ Ask</strong> button (bottom-right of every screen) answers questions from the app’s data and can set up tasks, notes and check-ins
          after the person presses Confirm. Super admins can ask about everything, including money. The key stays on the server; chats aren’t saved.
        </p>
        <div style={row}><label className="form-label" style={{ margin: 0 }}>Turned on</label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}><input type="checkbox" checked={s.enabled} onChange={e => set('enabled', e.target.checked)} /> Show the assistant</label></div>
        <div style={row}><label className="form-label" style={{ margin: 0 }}>Anthropic API key</label>
          <div>
            {s.keyFromEnv ? <span style={{ fontSize: '0.85rem' }}>Set on the server (.env) — ends in …{s.keyLast4}</span> : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input className="form-input" type="password" autoComplete="off" style={{ maxWidth: 340 }} value={key} onChange={e => setKey(e.target.value)}
                       placeholder={s.keySet ? `Saved — ends in …${s.keyLast4}. Paste a new one to replace it.` : 'sk-ant-…'} />
                {s.keySet && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => window.confirm('Remove the key? The assistant stops working until a new one is added.') && save({ clearKey: true })}>Remove key</button>}
              </div>
            )}
          </div></div>
        <div style={row}><label className="form-label" style={{ margin: 0 }}>Model</label>
          <select className="form-select" style={{ maxWidth: 380 }} value={s.model} onChange={e => set('model', e.target.value)}>
            {Object.entries(s.models).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></div>
        <div style={row}><label className="form-label" style={{ margin: 0 }}>Other staff (not super admins)</label>
          <select className="form-select" style={{ maxWidth: 380 }} value={s.staffAccess} onChange={e => set('staffAccess', e.target.value)}>
            <option value="off">No access</option>
            <option value="limited">Membership, contacts &amp; events only — no money</option>
          </select></div>
        <div style={row}><label className="form-label" style={{ margin: 0 }}>Questions per month (everyone)</label>
          <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input className="form-input" type="number" min="1" style={{ width: 110 }} value={s.monthlyLimit} onChange={e => set('monthlyLimit', e.target.value)} />
            <span style={{ fontSize: '0.82rem', color: 'var(--gray-600)' }}>{s.thisMonth.questions} asked this month</span>
          </span></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={() => save()} disabled={busy}>Save</button>
          <button className="btn btn-secondary" onClick={test} disabled={busy || !s.keySet}>Test the key</button>
        </div>
      </div>

      <div className="card" style={{ padding: 20, fontSize: '0.85rem' }}>
        <h4 style={{ marginTop: 0 }}>Setting up the key (once)</h4>
        <ol style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
          <li>Create an NJJBA account in the Anthropic Console (console.anthropic.com) and add a payment method / credit.</li>
          <li>Set a monthly <strong>spend limit</strong> there, so the cost can never run away.</li>
          <li>Create an <strong>API key</strong> named “NJJBA app”, copy it, paste it above and press Save, then Test the key.</li>
          <li>If the key is ever exposed, delete it in the Console and paste a new one here.</li>
        </ol>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Recent use</span></div>
        <div className="table-wrap" style={{ border: 'none', maxHeight: 360, overflow: 'auto' }}>
          <table>
            <thead><tr><th>When</th><th>Who</th><th>What</th><th>Looked at / did</th></tr></thead>
            <tbody>
              {log.length === 0 && <tr><td colSpan={4} style={{ color: 'var(--gray-500)' }}>Nothing yet.</td></tr>}
              {log.map(l => (
                <tr key={l.id}>
                  <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>{new Date(l.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                  <td style={{ fontSize: '0.82rem' }}>{[l.first_name, l.last_name].filter(Boolean).join(' ')}</td>
                  <td style={{ fontSize: '0.82rem' }}>{l.kind === 'action' ? <strong>✓ {l.question}</strong> : l.question}</td>
                  <td style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{l.tools}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
